import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { Button, Divider, List, ListItem, Paragraph, Separator } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import {
  generateSls,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { headers, R2AppName } from '../../../../../shared/services/config'
import { errorCollector } from '../../../../../shared/utils/utils'
import useRecords from '../../../../views/indexing/hooks/useRecords'
import RecordGroup from './RecordGroup'
import Droppable from '../../../../views/indexing/components/ui/Droppable'
import PersonDragOverlay from './PersonDragOverlay'
import { generateSaveDispatches } from './ManageNamesSaveUtils'
import { droppableContainerCss, activeBorderCss } from './style.utils'

const sensorsActivationConstraint = {
  delay: 150,
  distance: 5,
}

const ManageNamesContent = ({ setSelectedRecordId = () => {} }) => {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const dispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { openOverlay } = useOverlayContext()
  const [dragInfo, setDragInfo] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: sensorsActivationConstraint }),
    useSensor(TouchSensor, { activationConstraint: sensorsActivationConstraint })
  )

  const handleAddNameClick = (recordId = null) => {
    setSelectedRecordId(recordId)
    openOverlay('addNamesOverlay')
  }

  const { allRecords } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })

  const savePersonToRecord = useCallback(
    async (recordIdParam, person) => {
      try {
        const { currentImageArk, images } = zionSlsState

        const dispatches = await generateSaveDispatches({
          recordIdParam,
          personsData: person,
          currentPartitionKey: currentImageArk,
          images,
          recordType: '',
        })

        const sls = generateSls(dispatches)

        if (sls?.length) {
          saveSLSActions({
            actions: sls,
            userId: r2State.userId,
            appName: R2AppName,
            fsUserAgentChain: headers['FS-User-Agent-Chain'],
          })

          const newRecordId = dispatches.find((d) => d.type === 'CREATE_ELEMENT')?.value?.id
          dispatch({
            type: 'BATCH_DISPATCHES',
            value:
              recordIdParam === 'new_record'
                ? dispatches.concat({ type: 'NEW_RECORD_AT_TOP', value: newRecordId })
                : dispatches,
          })
        }
      } catch (error) {
        errorCollector(error, zionSlsState?.currentImageArk, {
          codeId: 30,
        })
      }
    },
    [zionSlsState, dispatch, r2State.userId]
  )

  const handleDragStart = useCallback(
    (event) => {
      const { active } = event
      const draggedPerson = getNestedOrFloatingElementById(zionSlsState.images, active?.id)
      const record = getNestedSubelementsById(allRecords, draggedPerson?.recordId)?.[0]
      setDragInfo({ isDragging: true, person: draggedPerson, record })
    },
    [zionSlsState.images, allRecords]
  )

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      const sourceRecordId = active?.data?.current?.record?.id
      const destinationRecordId = over?.id

      if (sourceRecordId === destinationRecordId || !destinationRecordId) {
        setDragInfo(null)
        return
      }

      const person = getNestedOrFloatingElementById(zionSlsState.images, active?.id)
      savePersonToRecord(destinationRecordId, person)
      setDragInfo(null)
    },
    [zionSlsState.images, savePersonToRecord]
  )

  const handleDragCancel = useCallback(() => {
    setDragInfo(null)
  }, [])

  const getNewRecordOverStyle = (isOver) => {
    if (!dragInfo?.isDragging) {
      return undefined
    }
    return isOver ? activeBorderCss : droppableContainerCss
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Separator size="xs" />
      <Paragraph size="sm">
        {t('manage-names.instructions', 'Moving a name will remove its previous relationships.')}
      </Paragraph>

      {/* New Group Section */}
      <Divider marginY="xs" />
      <Droppable id="new_record" setOverStyle={getNewRecordOverStyle}>
        <List>
          <ListItem
            primaryText={t('manage-names.new-group.heading', 'New Group')}
            secondaryText={t('manage-names.new-group.description', 'Add or drag name here to create a new group.')}
          />
        </List>
        <Button
          emphasis="low"
          size="sm"
          Icon={ContentAdd}
          data-testid="new-group-add-name-button"
          linkName="manage-names-new-group-add-name"
          onClick={() => handleAddNameClick(null)}
        >
          {t('manage-names.add-name', 'NAME')}
        </Button>
      </Droppable>

      {allRecords?.map((record) => (
        <RecordGroup key={record?.id} record={record} dragInfo={dragInfo} handleAddNameClick={handleAddNameClick} />
      ))}

      <PersonDragOverlay dragInfo={dragInfo} />
    </DndContext>
  )
}

export default ManageNamesContent
